import { DialogM } from "@common/components/alert/dialog/Dialog"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import moment from "moment"
import React, { useContext, useState } from "react"
import { TouchableOpacity, View } from "react-native"
import { TouchableRipple } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { DimensionsContext } from "../../../common/utils/mobileUtils"

function TutorScoreCardM(props) {
    const {
        tutorScore,
        previousMonthScore,
        globalScore,
        width,
        title = "Score",
        isAdmin = false,
        isMonthly = false,
        bonusInfo = null,
        trialConversionStats = null,
        previousMonthTrialConversionStats = null,
        globalTrialConversionStats = null,
        activityThreshold = 5,
    } = props

    const [periodName, setPeriodName] = useState("Ce mois-ci")
    const [period, setPeriod] = useState("current")

    const onPeriodPress = () => {
        if (period === "current" && previousMonthScore) {
            setPeriodName("Le mois dernier")
            setPeriod("previous")
        } else if (period === "previous" && globalScore) {
            setPeriodName("Depuis le début")
            setPeriod("global")
        } else {
            setPeriodName("Ce mois-ci")
            setPeriod("current")
        }
    }

    const displayedScore =
        period === "current"
            ? tutorScore
            : period === "previous"
              ? previousMonthScore
              : globalScore

    const displayedTrialConversionStats =
        period === "current"
            ? trialConversionStats
            : period === "previous"
              ? previousMonthTrialConversionStats
              : globalTrialConversionStats

    // Lives progress for monthly scores
    const livesCount = displayedScore?.lives_count || 0
    const livesProgress = Math.min(livesCount / activityThreshold, 1)

    // Check if punctuality is below 50% (only for monthly scores)
    const punctualityScore = displayedScore?.punctuality_normalized || 0
    const isPunctualityLow = punctualityScore < 0.5 && livesCount > 0

    // Date range for the selected period
    const startDate =
        period === "current"
            ? moment().startOf("month").format("DD MMM")
            : period === "previous"
              ? moment().add(-1, "month").startOf("month").format("DD MMM")
              : null
    const endDate =
        period === "current"
            ? moment().format("DD MMM")
            : period === "previous"
              ? moment().add(-1, "month").endOf("month").format("DD MMM")
              : null

    const handleSatisfactionInfo = () => {
        DialogM.show({
            text1: "Satisfaction élève",
            text2: `Après chaque séance, l'élève peut laisser une note de satisfaction.\n\n• Moy. = la moyenne des notes reçues sur la période\n• Exprimés = le % d'élèves qui ont réellement donné une note`,
            champion: "zoe",
            buttonText1: "Compris",
            buttonColor1: theme.colors.primary,
            onPress: () => {},
        })
    }

    const handleInteractionInfo = () => {
        DialogM.show({
            text1: "Interaction élève",
            text2: `Mesure le niveau d'échanges et le bon équilibre entre tes élèves pendant la séance.\n\nCe score combine :\n• Tes interactions vocales (mode dialogue, questions/réponses, participation)\n• Ta répartition d'attention (ne pas laisser un élève trop longtemps seul, répondre aux mains levées)\n\nPlus tu fais parler chaque élève et restes attentif à chacun, plus le score monte.`,
            champion: "tina",
            buttonText1: "Compris",
            buttonColor1: theme.colors.primary,
            onPress: () => {},
        })
    }

    const handleProductivityInfo = () => {
        DialogM.show({
            text1: "Productivité élève",
            text2: `Basée sur les activités soumises par l'élève (exercices réalisés et envoyés via l'app) et le volume de texte écrit reconnu.\n\nBonnes pratiques : privilégier des exercices courts et faire soumettre régulièrement (ça augmente la productivité + permet plus de feedbacks).`,
            champion: "sam",
            buttonText1: "Compris",
            buttonColor1: theme.colors.primary,
            onPress: () => {},
        })
    }

    const handleTrialConversionInfo = () => {
        const message = isAdmin
            ? `Part des élèves rencontrés en séance d'essai qui ont ensuite activé un abonnement.\n\nPlus le tuteur est performant.e lors de la séance d'essai, plus les familles s'abonnent ensuite.`
            : `Part des élèves que tu as rencontrés en séance d'essai qui ont ensuite activé leur abonnement.\n\nPlus tu es performant.e lors de la séance d'essai, plus les familles s'abonnent ensuite.`

        DialogM.show({
            text1: "Conversion des essais",
            text2: message,
            champion: "zoe",
            buttonText1: "Compris",
            buttonColor1: theme.colors.primary,
            onPress: () => {},
        })
    }

    const handlePunctualityInfo = () => {
        let message = `Reflète ta régularité et ton engagement pendant les séances : être à l'heure, garder ta caméra allumée et utiliser un équipement adapté (ordinateur ou tablette).\n\nPlus tu es ponctuel, présent et bien installé, plus le score monte.`

        // Add warning if punctuality is low
        if (isPunctualityLow) {
            const warningMessage = isAdmin
                ? `\n\n⚠️ Le score d'assiduité de ce tuteur est en dessous de 50%. Les absences et/ou retards fréquents affectent son classement et l'expérience des élèves.`
                : `\n\n⚠️ Ton score d'assiduité est en dessous de 50%. Tes absences et/ou retards fréquents affectent négativement ton classement et l'expérience de tes élèves.`
            message += warningMessage
        }

        DialogM.show({
            text1: "Assiduité et cadre",
            text2: message,
            champion: "abou",
            variant: isPunctualityLow ? "unhappy" : undefined,
            buttonText1: "Compris",
            buttonColor1: theme.colors.primary,
            onPress: () => {},
        })
    }

    const rank = displayedScore?.rank
    const rankText = rank != null ? `#${rank}` : null

    const dimensions = useContext(DimensionsContext)

    const styles = {
        metricContainer: {
            flex: 1,
            alignItems: "center",
            marginHorizontal: 4,
        },
        metricLabel: {
            textAlign: "center",
        },
        metricValue: {
            textAlign: "center",
        },
        metricSubtext: {
            textAlign: "center",
        },
    }

    return (
        (tutorScore || previousMonthScore) && (
            <>
                {title && (
                    <TextM
                        color={theme.colors.primary}
                        fontWeight={"Bold"}
                        fontSize={16}
                        wrap
                    >
                        {title}
                    </TextM>
                )}
                <View
                    style={{
                        width: width ?? "80%",
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: theme.colors.lightMore,
                        backgroundColor: theme.colors.white,
                        padding: 20,
                        marginTop: 10,
                        minHeight: 100,
                        marginBottom: 20,
                    }}
                >
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 16,
                        }}
                    >
                        <TextM fontWeight={"Bold"} fontSize={20} wrap>
                            {"Mon score masteur"}
                        </TextM>
                        <TextM fontSize={11} color={theme.colors.grey} wrap>
                            {displayedScore?.created_at && period === "current"
                                ? new Date(
                                      displayedScore.created_at
                                  ).toLocaleDateString("fr-FR")
                                : ""}
                        </TextM>
                    </View>

                    {isMonthly && (previousMonthScore || globalScore) && (
                        <>
                            <TouchableRipple
                                onPress={onPeriodPress}
                                style={{
                                    borderWidth: 1,
                                    borderColor: theme.colors.lightMore,
                                    backgroundColor: theme.colors.white,
                                    borderRadius: 10,
                                    alignItems: "center",
                                    width:
                                        dimensions.width > 800
                                            ? dimensions?.width / 2
                                            : dimensions?.width - 20,
                                    paddingVertical: 5,
                                    marginTop: 20,
                                    alignSelf: "center",
                                }}
                            >
                                <TextM
                                    color={theme.colors.grey}
                                    fontWeight="Medium"
                                >
                                    {periodName.toUpperCase()}
                                </TextM>
                            </TouchableRipple>
                            {startDate && endDate && (
                                <TextM
                                    fontWeight={"Light"}
                                    fontSize={12}
                                    color={theme.colors.grey}
                                    style={{
                                        textAlign: "center",
                                        marginBottom: 16,
                                    }}
                                    wrap
                                >
                                    Du {startDate} au {endDate}
                                </TextM>
                            )}
                            {period === "global" && (
                                <TextM
                                    fontWeight={"Light"}
                                    fontSize={12}
                                    color={theme.colors.grey}
                                    style={{
                                        textAlign: "center",
                                        marginBottom: 16,
                                    }}
                                    wrap
                                >
                                    Score cumulé depuis le début
                                </TextM>
                            )}
                        </>
                    )}

                    <View
                        style={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                            marginBottom: 16,
                        }}
                    >
                        {/* Score Global Block */}
                        <View
                            style={{
                                backgroundColor: theme.colors.primary + "11",
                                padding: 12,
                                borderRadius: 8,
                                alignItems: "center",
                                flex: 1,
                                marginRight: 8,
                            }}
                        >
                            <TextM
                                fontSize={24}
                                fontWeight={"Bold"}
                                color={theme.colors.primary}
                            >
                                {displayedScore?.performance_score != null
                                    ? `${
                                          (
                                              displayedScore.performance_score *
                                              1000
                                          ).toFixed(0) / 10
                                      }%`
                                    : "-"}
                            </TextM>
                            <TextM fontSize={12} color={theme.colors.grey} wrap>
                                Score global
                            </TextM>
                        </View>

                        {/* Rank Block - Only show when threshold reached */}
                        {rankText &&
                            (livesCount >= activityThreshold || !isMonthly) && (
                                <View
                                    style={{
                                        backgroundColor:
                                            theme.colors.info + "11",
                                        padding: 12,
                                        borderRadius: 8,
                                        alignItems: "center",
                                        flex: 1,
                                        marginLeft: 8,
                                    }}
                                >
                                    <TextM
                                        fontSize={24}
                                        fontWeight={"Bold"}
                                        color={theme.colors.info}
                                    >
                                        {rankText}
                                    </TextM>
                                    <TextM
                                        fontSize={12}
                                        color={theme.colors.grey}
                                        wrap
                                    >
                                        Classement
                                    </TextM>
                                </View>
                            )}
                    </View>

                    {/* Bonus Indicator - Only show when bonus is active and viewing current month */}
                    {bonusInfo?.has_bonus &&
                        bonusInfo?.is_active &&
                        period === "current" && (
                            <View
                                style={{
                                    marginBottom: 16,
                                    backgroundColor:
                                        theme.colors.success + "11",
                                    borderWidth: 1,
                                    borderColor: theme.colors.success,
                                    paddingVertical: 8,
                                    paddingHorizontal: 12,
                                    borderRadius: 8,
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <FontAwesome5
                                    name="check-circle"
                                    size={16}
                                    color={theme.colors.success}
                                    solid
                                    style={{ marginRight: 8 }}
                                />
                                <TextM
                                    fontSize={13}
                                    fontWeight={"SemiBold"}
                                    color={theme.colors.success}
                                    wrap
                                >
                                    Bonus Top 3 actif
                                </TextM>
                            </View>
                        )}

                    {/* Trial Conversion Rate - Only for trial tutors with completed trials in the period */}
                    {displayedTrialConversionStats?.completed_trials_count >
                        0 && (
                        <TouchableOpacity
                            onPress={handleTrialConversionInfo}
                            style={{
                                marginBottom: 12,
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <TextM
                                fontSize={11}
                                color={theme.colors.grey}
                                wrap
                            >
                                Conversion essais :{" "}
                                {displayedTrialConversionStats.conversion_rate !=
                                null
                                    ? `${(displayedTrialConversionStats.conversion_rate * 100).toFixed(0)}%`
                                    : "-"}{" "}
                                (
                                {
                                    displayedTrialConversionStats.converted_trials_count
                                }
                                /
                                {
                                    displayedTrialConversionStats.completed_trials_count
                                }
                                )
                            </TextM>
                            <FontAwesome5
                                name="info-circle"
                                size={11}
                                color={theme.colors.grey}
                                style={{ marginLeft: 4 }}
                            />
                        </TouchableOpacity>
                    )}

                    <View
                        style={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                            marginBottom: 12,
                        }}
                    >
                        <TouchableOpacity
                            style={styles.metricContainer}
                            onPress={handleSatisfactionInfo}
                        >
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <TextM
                                    fontWeight={"SemiBold"}
                                    fontSize={12}
                                    color={theme.colors.primary}
                                    style={styles.metricLabel}
                                    wrap
                                >
                                    Satisfaction élève
                                </TextM>
                                <FontAwesome5
                                    name="info-circle"
                                    size={12}
                                    color={theme.colors.primary}
                                    style={{ marginLeft: 4 }}
                                />
                            </View>
                            <TextM
                                fontWeight={"Bold"}
                                fontSize={16}
                                style={styles.metricValue}
                            >
                                {displayedScore?.satisfaction_normalized != null
                                    ? `${(
                                          displayedScore.satisfaction_normalized *
                                          100
                                      ).toFixed(0)}%`
                                    : "-"}
                            </TextM>
                            <TextM
                                fontSize={11}
                                color={theme.colors.grey}
                                style={styles.metricSubtext}
                                wrap
                            >
                                Moy:{" "}
                                {displayedScore?.average_satisfaction ?? "-"}
                            </TextM>
                            <TextM
                                fontSize={11}
                                color={theme.colors.grey}
                                style={styles.metricSubtext}
                                wrap
                            >
                                Exprimés:{" "}
                                {displayedScore?.satisfaction_details
                                    ?.response_rate != null
                                    ? `${(
                                          displayedScore.satisfaction_details
                                              .response_rate * 100
                                      ).toFixed(0)}%`
                                    : "-"}
                            </TextM>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.metricContainer}
                            onPress={handleInteractionInfo}
                        >
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <TextM
                                    fontWeight={"SemiBold"}
                                    fontSize={12}
                                    color={theme.colors.primary}
                                    style={styles.metricLabel}
                                    wrap
                                >
                                    Interaction élève
                                </TextM>
                                <FontAwesome5
                                    name="info-circle"
                                    size={12}
                                    color={theme.colors.primary}
                                    style={{ marginLeft: 4 }}
                                />
                            </View>
                            <TextM
                                fontWeight={"Bold"}
                                fontSize={16}
                                style={styles.metricValue}
                            >
                                {displayedScore?.interaction_normalized != null
                                    ? `${(
                                          displayedScore.interaction_normalized *
                                          100
                                      ).toFixed(0)}%`
                                    : "-"}
                            </TextM>
                            <TextM
                                fontSize={11}
                                color={theme.colors.grey}
                                style={styles.metricSubtext}
                                wrap
                            >
                                Moy:{" "}
                                {displayedScore?.average_interaction ?? "-"}
                                /30
                            </TextM>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.metricContainer}
                            onPress={handleProductivityInfo}
                        >
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <TextM
                                    fontWeight={"SemiBold"}
                                    fontSize={12}
                                    color={theme.colors.primary}
                                    style={styles.metricLabel}
                                    wrap
                                >
                                    Productivité élève
                                </TextM>
                                <FontAwesome5
                                    name="info-circle"
                                    size={12}
                                    color={theme.colors.primary}
                                    style={{ marginLeft: 4 }}
                                />
                            </View>
                            <TextM
                                fontWeight={"Bold"}
                                fontSize={16}
                                style={styles.metricValue}
                            >
                                {displayedScore?.productivity_normalized != null
                                    ? `${(
                                          displayedScore.productivity_normalized *
                                          100
                                      ).toFixed(0)}%`
                                    : "-"}
                            </TextM>
                            <TextM
                                fontSize={11}
                                color={theme.colors.grey}
                                style={styles.metricSubtext}
                                wrap
                            >
                                Moy:{" "}
                                {displayedScore?.average_productivity ?? "-"}
                                /30
                            </TextM>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.metricContainer}
                            onPress={handlePunctualityInfo}
                        >
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <TextM
                                    fontWeight={"SemiBold"}
                                    fontSize={12}
                                    color={
                                        isPunctualityLow
                                            ? theme.colors.error
                                            : theme.colors.primary
                                    }
                                    style={styles.metricLabel}
                                    wrap
                                >
                                    Assiduité et cadre
                                </TextM>
                                {isPunctualityLow ? (
                                    <FontAwesome5
                                        name="exclamation-circle"
                                        size={12}
                                        color={theme.colors.error}
                                        solid
                                        style={{ marginLeft: 4 }}
                                    />
                                ) : (
                                    <FontAwesome5
                                        name="info-circle"
                                        size={12}
                                        color={theme.colors.primary}
                                        style={{ marginLeft: 4 }}
                                    />
                                )}
                            </View>
                            <TextM
                                fontWeight={"Bold"}
                                fontSize={16}
                                style={styles.metricValue}
                                color={
                                    isPunctualityLow
                                        ? theme.colors.error
                                        : undefined
                                }
                            >
                                {displayedScore?.punctuality_normalized != null
                                    ? `${(
                                          displayedScore.punctuality_normalized *
                                          100
                                      ).toFixed(0)}%`
                                    : "-"}
                            </TextM>
                            <TextM
                                fontSize={11}
                                color={
                                    isPunctualityLow
                                        ? theme.colors.error
                                        : theme.colors.grey
                                }
                                style={styles.metricSubtext}
                                wrap
                            >
                                À l&apos;heure:{" "}
                                {displayedScore?.punctuality_details
                                    ?.on_time_sessions ?? "-"}
                                /
                                {displayedScore?.punctuality_details
                                    ?.total_sessions ?? "-"}
                            </TextM>
                            <TextM
                                fontSize={11}
                                color={
                                    isPunctualityLow
                                        ? theme.colors.error
                                        : theme.colors.grey
                                }
                                style={styles.metricSubtext}
                                wrap
                            >
                                Absences:{" "}
                                {displayedScore?.punctuality_details
                                    ?.absence_sessions ?? "-"}
                            </TextM>
                        </TouchableOpacity>
                    </View>

                    {/* Lives and Students Count - Show monthly data if isMonthly, otherwise all-time */}
                    <View
                        style={{
                            marginTop: 12,
                            paddingTop: 12,
                            borderTopWidth: 1,
                            borderTopColor: theme.colors.lightMore,
                        }}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                justifyContent: "space-around",
                            }}
                        >
                            <View style={{ alignItems: "center" }}>
                                <TextM
                                    fontSize={16}
                                    fontWeight={"Bold"}
                                    color={theme.colors.primary}
                                >
                                    {isMonthly && period !== "global"
                                        ? (displayedScore?.seniority_smoothing
                                              ?.lives_count_monthly ?? "-")
                                        : (displayedScore?.seniority_smoothing
                                              ?.lives_count ?? "-")}
                                </TextM>
                                <TextM
                                    fontSize={11}
                                    color={theme.colors.grey}
                                    wrap
                                >
                                    Lives
                                </TextM>
                            </View>
                            <View style={{ alignItems: "center" }}>
                                <TextM
                                    fontSize={16}
                                    fontWeight={"Bold"}
                                    color={theme.colors.primary}
                                >
                                    {isMonthly && period !== "global"
                                        ? (displayedScore?.seniority_smoothing
                                              ?.unique_students_monthly ?? "-")
                                        : (displayedScore?.seniority_smoothing
                                              ?.unique_students ?? "-")}
                                </TextM>
                                <TextM
                                    fontSize={11}
                                    color={theme.colors.grey}
                                    wrap
                                >
                                    Élèves
                                </TextM>
                            </View>
                        </View>
                    </View>
                    {/* Lives Progress Indicator - Only for monthly scores and when threshold not reached */}
                    {isMonthly &&
                        period !== "global" &&
                        livesCount < activityThreshold && (
                            <View
                                style={{
                                    marginTop: 16,
                                    backgroundColor:
                                        theme.colors.lightMore + "44",
                                    padding: 12,
                                    borderRadius: 8,
                                }}
                            >
                                <View
                                    style={{
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 8,
                                    }}
                                >
                                    <TextM
                                        fontSize={12}
                                        fontWeight={"SemiBold"}
                                        color={theme.colors.primary}
                                    >
                                        Progression
                                    </TextM>
                                    <TextM
                                        fontSize={12}
                                        fontWeight={"Bold"}
                                        color={theme.colors.primary}
                                    >
                                        {livesCount} / {activityThreshold}
                                    </TextM>
                                </View>

                                {/* Progress Bar */}
                                <View
                                    style={{
                                        height: 8,
                                        backgroundColor: theme.colors.white,
                                        borderRadius: 4,
                                        overflow: "hidden",
                                        marginBottom: 6,
                                    }}
                                >
                                    <View
                                        style={{
                                            height: "100%",
                                            width: `${livesProgress * 100}%`,
                                            backgroundColor:
                                                theme.colors.primary,
                                            borderRadius: 4,
                                        }}
                                    />
                                </View>

                                <TextM
                                    fontSize={10}
                                    color={theme.colors.grey}
                                    wrap
                                >
                                    Complète {activityThreshold} lives pour
                                    débloquer le classement et ton score complet
                                </TextM>
                            </View>
                        )}
                </View>
            </>
        )
    )
}

const mapStateToProps = (state) => ({
    activityThreshold: state.data.staticData?.monthly_activity_threshold || 5,
})

export default connect(mapStateToProps)(TutorScoreCardM)
